<template>
  <el-container class="layout-wrap">
    <!-- 侧边栏 -->
    <el-aside width="220px" class="aside">
      <div class="logo">炫舞小酒馆管理后台</div>
      <el-menu
        :default-active="$route.path"
        router
        background-color="#001529"
        text-color="#ffffffa0"
        active-text-color="#ffffff"
      >
        <template v-for="node in menu" :key="node.path || node.title">
          <el-menu-item v-if="node.type === 'item'" :index="node.path">
            <el-icon><component :is="iconMap[node.icon]" /></el-icon>
            <span>{{ node.title }}</span>
          </el-menu-item>
          <el-sub-menu v-else :index="node.title">
            <template #title>
              <el-icon><component :is="iconMap[node.icon]" /></el-icon>
              <span>{{ node.title }}</span>
            </template>
            <el-menu-item v-for="c in node.children" :key="c.path" :index="c.path">{{ c.title }}</el-menu-item>
          </el-sub-menu>
        </template>
      </el-menu>
    </el-aside>

    <!-- 主内容区 -->
    <el-container>
      <el-header class="header">
        <span class="page-title">{{ $route.meta.title }}</span>
        <div class="header-right">
          <span class="username" @click="openPwd" title="点击修改密码">
            <el-icon><User /></el-icon>
            {{ username }}
          </span>
          <el-button size="small" @click="logout">退出登录</el-button>
        </div>
      </el-header>
      <el-main class="main">
        <router-view />
      </el-main>
    </el-container>

    <!-- 修改密码 -->
    <el-dialog v-model="pwdVisible" title="修改密码" width="420px">
      <el-form :model="pwdForm" :rules="pwdRules" ref="pwdRef" label-width="88px">
        <el-form-item label="原密码" prop="oldPwd">
          <el-input v-model="pwdForm.oldPwd" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPwd">
          <el-input v-model="pwdForm.newPwd" type="password" show-password placeholder="至少4位" />
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPwd">
          <el-input v-model="pwdForm.confirmPwd" type="password" show-password placeholder="再次输入新密码" @keyup.enter="submitPwd" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="pwdVisible = false">取消</el-button>
        <el-button type="primary" :loading="pwdLoading" @click="submitPwd">保存</el-button>
      </template>
    </el-dialog>
  </el-container>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { ElNotification, ElMessage } from 'element-plus'
import md5 from 'md5'
import { Monitor, DataLine, TrendCharts, Setting, Money, User, Promotion } from '@element-plus/icons-vue'
import { getServiceCalls, handleServiceCall, getMe, changeMyPassword } from '@/api'
import { visibleMenu } from '@/menu'
import { clearAuth, setPerms } from '@/utils/perm'

const router = useRouter()
const username = ref(localStorage.getItem('admin_username') || '管理员')

const iconMap = { Monitor, DataLine, TrendCharts, Setting, Money, User, Promotion }
const menu = ref(visibleMenu())

function logout() {
  clearAuth()
  router.push('/login')
}

/* ---------- 修改密码 ---------- */
const pwdVisible = ref(false)
const pwdLoading = ref(false)
const pwdRef = ref()
const pwdForm = reactive({ oldPwd: '', newPwd: '', confirmPwd: '' })
const pwdRules = {
  oldPwd: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
  newPwd: [{ required: true, min: 4, message: '新密码至少4位', trigger: 'blur' }],
  confirmPwd: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    { validator: (r, v, cb) => (v === pwdForm.newPwd ? cb() : cb(new Error('两次密码不一致'))), trigger: 'blur' },
  ],
}

function openPwd() {
  pwdForm.oldPwd = ''
  pwdForm.newPwd = ''
  pwdForm.confirmPwd = ''
  pwdVisible.value = true
  pwdRef.value?.clearValidate()
}

async function submitPwd() {
  try {
    await pwdRef.value.validate()
    pwdLoading.value = true
    await changeMyPassword({ old_password: md5(pwdForm.oldPwd), new_password: md5(pwdForm.newPwd) })
    pwdVisible.value = false
    ElMessage.success('密码已修改，请重新登录')
    setTimeout(() => { clearAuth(); router.push('/login') }, 800)
  } catch {
    // 校验/接口错误已处理
  } finally {
    pwdLoading.value = false
  }
}

/* ---------- 呼叫服务员：全局轮询 + 常驻通知 ---------- */
// 每次呼叫一条独立通知，右上角堆叠累加；关闭通知 = 标记已处理，处理后不再弹出
const shownCalls = new Set() // 已弹出过的呼叫 id，避免重复弹
let callTimer = null

async function pollServiceCalls() {
  try {
    const calls = (await getServiceCalls()) || []
    calls.forEach((call) => {
      if (shownCalls.has(call.id)) return
      shownCalls.add(call.id)
      let handled = false
      const resolve = async () => {
        if (handled) return
        handled = true
        try { await handleServiceCall(call.id) } catch { /* 拦截器已提示 */ }
      }
      const inst = ElNotification({
        title: '🔔 呼叫服务员',
        message: `${call.room_name} 请求服务员，点击右上角 × 处理`,
        type: 'warning',
        duration: 0,       // 不自动关闭，堆叠累加
        showClose: true,   // 可手动关闭；关闭即处理
        position: 'top-right',
        onClose: resolve,           // 点 × 关闭 → 标记已处理
        onClick: () => inst.close(), // 点内容也关闭（触发 onClose 处理）
      })
    })
  } catch { /* 静默 */ }
}

onMounted(async () => {
  // 刷新权限（防止 localStorage 过期或被超管改过），并重建菜单
  try {
    const me = await getMe()
    setPerms(me)
    menu.value = visibleMenu()
    username.value = me.nickname || me.username || username.value
  } catch { /* token 失效由拦截器跳登录 */ }
  pollServiceCalls()
  callTimer = setInterval(pollServiceCalls, 5000)
})
onBeforeUnmount(() => clearInterval(callTimer))
</script>

<style scoped>
.layout-wrap { height: 100vh; }
.aside { background: #001529; }
.logo {
  height: 60px;
  line-height: 60px;
  text-align: center;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
  border-bottom: 1px solid #ffffff20;
}
.header {
  background: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
}
.page-title { font-size: 16px; font-weight: 600; color: #303133; }
.header-right { display: flex; align-items: center; gap: 12px; }
.username {
  font-size: 14px; color: #606266; cursor: pointer;
  display: inline-flex; align-items: center; gap: 4px;
}
.username:hover { color: #409eff; }
.main { background: #f5f7fa; }
</style>
